import { ChangeDetectionStrategy, Component, DestroyRef, computed, inject, OnInit, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormBuilder, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import { NgSelectComponent } from '@ng-select/ng-select';
import { ModalBodyComponent, ModalComponent, ModalFooterComponent, ModalFooterAction, ModalHeaderComponent, StateMessageComponent } from '@groundworks/ngx-groundworks-ui';
import { CancelledReasonInfo } from '../../sdk/sdk';
import { CancellationReasonService } from '../../services/cancellation-reason.service';
import { JobService } from '../../services/job.service';

@Component({
  selector: 'app-job-cancel-modal',
  imports: [ReactiveFormsModule, NgSelectComponent, ModalComponent, ModalHeaderComponent, ModalBodyComponent, ModalFooterComponent, StateMessageComponent],
  templateUrl: './job-cancel-modal.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  styles: `
    ng-select.ng-select-invalid ::ng-deep .ng-select-container {
      border-color: var(--bs-danger);
    }
  `,
})
export class JobCancelModalComponent implements OnInit {
  // Writable signals used instead of input() because NgbModal sets values
  // imperatively via componentInstance — signal inputs are read-only.
  readonly title = signal('Cancel Job');
  readonly jobId = signal('');
  readonly message = signal('Are you sure you want to cancel this job and all of its projects? This cannot be undone and will result in all projects being automatically removed from the schedule.');
  readonly confirmButtonText = signal('Yes, Cancel Entire Job');
  readonly cancelButtonText = signal('No, Go Back');
  readonly cancelButtonErrorText = signal('Go Back');
  readonly alertClasses = signal('alert-danger');
  private readonly activeModal = inject(NgbActiveModal);
  private readonly fb = inject(FormBuilder);
  private readonly cancellationReasonService = inject(CancellationReasonService);
  private readonly jobService = inject(JobService);
  private readonly destroyRef = inject(DestroyRef);

  readonly loading = signal(false);
  readonly hasError = signal(false);

  readonly cancellationReasons = signal<CancelledReasonInfo[]>([]);

  readonly form: FormGroup = this.fb.group({
    cancellationReason: [null as CancelledReasonInfo | null, Validators.required],
    comment: [''],
  });

  readonly submitted = signal(false);

  readonly footerActions = computed<ModalFooterAction[]>(() => {
    const actions: ModalFooterAction[] = [
      {
        label: this.hasError() ? this.cancelButtonErrorText() : this.cancelButtonText(),
        type: 'outline-secondary',
        action: () => this.cancel(),
      },
    ];

    if (!this.hasError()) {
      actions.push({
        label: this.confirmButtonText(),
        type: 'danger',
        action: () => this.confirm(),
        loading: () => this.loading(),
        disabled: () => this.loading(),
      });
    }

    return actions;
  });

  ngOnInit(): void {
    this.cancellationReasonService.getCancellationReasons()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(reasons => this.cancellationReasons.set(reasons));
  }

  confirm(): void {
    this.submitted.set(true);
    if (this.form.invalid) return;
    this.loading.set(true);
    const reason: CancelledReasonInfo = this.form.value.cancellationReason;
    const comment: string = this.form.value.comment;
    this.jobService.cancelJob(this.jobId(), reason.id, comment)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: () => {
          this.loading.set(false);
          this.activeModal.close(true);
        },
        error: () => {
          this.loading.set(false);
          this.hasError.set(true);
        }
      });
  }

  cancel(): void {
    this.activeModal.dismiss(false);
  }
}
